import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    Copy, ArrowRight, Play, Pause, ShieldCheck,
    AlertTriangle, FileText, Image, Video, CheckCircle,
    XCircle, Activity, Server, Link, AtSign, Languages
} from 'lucide-react';
import { GlassButton } from '../components/ui/GlassButton';
import { Toast } from '../Toast';
import { IDFinderModal } from '../IDFinderModal';

interface HotClonerProps {
    selectedAccountId: number | string | null;
    onOpenIDFinder: () => void;
}

interface CloneStatus {
    active: boolean;
    source_id: string;
    dest_id: string;
    processed_count: number;
    total_estimated: number;
    status: 'running' | 'paused' | 'completed' | 'stopped' | 'error' | 'idle';
    last_message?: string;
    errors_count?: number;
    last_error_message?: string;
}

export const HotCloner: React.FC<HotClonerProps> = ({ selectedAccountId, onOpenIDFinder }) => {
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'info' | 'error' } | null>(null);

    // Form State
    const [sourceChat, setSourceChat] = useState('');
    const [destChat, setDestChat] = useState('');
    const [contentTypes, setContentTypes] = useState({
        text: true,
        image: true,
        video: true
    });
    const [replacements, setReplacements] = useState({
        enableLinkReplacement: false,
        newLink: '',
        enableUserReplacement: false,
        newUser: '',
        enableTranslation: false,
        targetLanguage: ''
    });

    // Validated Info (nomes dos chats para exibição)
    const [sourceName, setSourceName] = useState('');
    const [destName, setDestName] = useState('');

    // Status do Job Atual
    const [status, setStatus] = useState<CloneStatus | null>(null);
    const [localIDFinderOpen, setLocalIDFinderOpen] = useState(false);
    const [finderTarget, setFinderTarget] = useState<'source' | 'dest' | null>(null);

    useEffect(() => {
        if (selectedAccountId) {
            fetchStatus();
            const interval = setInterval(fetchStatus, 3000); // Poll status every 3s
            return () => clearInterval(interval);
        }
    }, [selectedAccountId]);

    const fetchStatus = async () => {
        if (!selectedAccountId) return;
        try {
            const res = await api.get(`/hot-cloner/status?account_id=${selectedAccountId}`);
            setStatus(res.data);
        } catch (e) {
            console.error("Erro ao buscar status do HotCloner", e);
        }
    };

    const handleStart = async () => {
        if (!selectedAccountId) return;
        if (!sourceChat || !destChat) {
            setToast({ msg: "Defina a Origem e o Destino.", type: 'error' });
            return;
        }

        setLoading(true);
        try {
            await api.post('/hot-cloner/start', {
                account_id: selectedAccountId,
                source_id: sourceChat,
                dest_id: destChat,
                filters: contentTypes,
                replacements: replacements
            });
            setToast({ msg: "Clonagem iniciada em Modo Seguro!", type: 'success' });
            fetchStatus();
        } catch (e: any) {
            const msg = e.response?.data?.error || e.message;
            setToast({ msg: "Erro ao iniciar: " + msg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleStop = async () => {
        try {
            await api.post('/hot-cloner/stop', { account_id: selectedAccountId });
            setToast({ msg: "Clonagem parada.", type: 'info' });
            fetchStatus();
        } catch (e) {
            console.error(e);
        }
    };

    const isRunning = status?.status === 'running' || status?.status === 'paused';
    const statusLabel = status?.status === 'stopped'
        ? 'Operação parada'
        : status?.status === 'completed'
            ? 'Concluída'
            : status?.status === 'error'
                ? 'Erro'
                : status?.status === 'running'
                    ? 'Processando em lote seguro...'
                    : status?.status === 'paused'
                        ? 'Pausada'
                        : status?.status
                            ? `Status: ${status.status}`
                            : '';

    return (
        <div className="dashboard-content">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            {/* Top Bar */}
            <div className="top-bar">
                <div>
                    <h1 className="page-title">HotCloner</h1>
                    <p className="page-subtitle" style={{ color: 'var(--text-muted)' }}>Clonagem segura de conteúdo entre canais</p>
                </div>
            </div>

            {!selectedAccountId ? null : (
                <div className="main-grid split-layout">

                    {/* Painel de Controle (Esquerda) */}
                    <div className="section-card form-panel">
                        <div className="card-header">
                            <span className="card-title">Configurar Clonagem</span>
                            <GlassButton onClick={() => { setLocalIDFinderOpen(true); setFinderTarget(null); }} style={{ height: '32px', fontSize: '12px', width: 'auto' }}>
                                Descobrir IDs
                            </GlassButton>
                        </div>

                        {/* Origem / Destino */}
                        <div className="input-group-pro">
                            <label>ORIGEM (De onde copiar)</label>
                            <div className="input-wrapper">
                                <Copy size={16} />
                                <input
                                    placeholder="ID ou Link do Canal/Grupo"
                                    value={sourceChat}
                                    onChange={e => setSourceChat(e.target.value)}
                                    disabled={isRunning}
                                />
                                <button className="mini-action-btn" onClick={() => { setLocalIDFinderOpen(true); setFinderTarget('source'); }} style={{ background: 'transparent', border: 'none', color: 'var(--accent-orange)', cursor: 'pointer', fontWeight: 600 }}>
                                    Busca
                                </button>
                            </div>
                        </div>

                        <div className="input-group-pro">
                            <label>DESTINO (Para onde enviar)</label>
                            <div className="input-wrapper">
                                <ArrowRight size={16} />
                                <input
                                    placeholder="ID ou Link do Canal/Grupo"
                                    value={destChat}
                                    onChange={e => setDestChat(e.target.value)}
                                    disabled={isRunning}
                                />
                                <button className="mini-action-btn" onClick={() => { setLocalIDFinderOpen(true); setFinderTarget('dest'); }} style={{ background: 'transparent', border: 'none', color: 'var(--accent-orange)', cursor: 'pointer', fontWeight: 600 }}>
                                    Busca
                                </button>
                            </div>
                        </div>

                        <div className="divider-line" style={{ height: '1px', background: 'var(--border-dark)', margin: '24px 0' }} />

                        {/* Filtros */}
                        <div className="config-section" style={{ marginBottom: '24px' }}>
                            <label className="section-label" style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500, marginBottom: '12px' }}>O que copiar?</label>
                            <div className="checkbox-row" style={{ display: 'flex', gap: '12px' }}>
                                <div
                                    className={`checkbox-card ${contentTypes.text ? 'checked' : ''}`}
                                    onClick={() => !isRunning && setContentTypes({ ...contentTypes, text: !contentTypes.text })}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: contentTypes.text ? 'rgba(255, 106, 0, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                        border: `1px solid ${contentTypes.text ? 'var(--accent-orange)' : 'var(--border-light)'}`,
                                        borderRadius: '8px',
                                        cursor: isRunning ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s',
                                        color: contentTypes.text ? '#fff' : 'var(--text-muted)'
                                    }}
                                >
                                    {contentTypes.text ? <CheckCircle size={18} color="var(--accent-orange)" /> : <FileText size={18} />}
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>Texto</span>
                                </div>

                                <div
                                    className={`checkbox-card ${contentTypes.image ? 'checked' : ''}`}
                                    onClick={() => !isRunning && setContentTypes({ ...contentTypes, image: !contentTypes.image })}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: contentTypes.image ? 'rgba(255, 106, 0, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                        border: `1px solid ${contentTypes.image ? 'var(--accent-orange)' : 'var(--border-light)'}`,
                                        borderRadius: '8px',
                                        cursor: isRunning ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s',
                                        color: contentTypes.image ? '#fff' : 'var(--text-muted)'
                                    }}
                                >
                                    {contentTypes.image ? <CheckCircle size={18} color="var(--accent-orange)" /> : <Image size={18} />}
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>Imagens</span>
                                </div>

                                <div
                                    className={`checkbox-card ${contentTypes.video ? 'checked' : ''}`}
                                    onClick={() => !isRunning && setContentTypes({ ...contentTypes, video: !contentTypes.video })}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: contentTypes.video ? 'rgba(255, 106, 0, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                        border: `1px solid ${contentTypes.video ? 'var(--accent-orange)' : 'var(--border-light)'}`,
                                        borderRadius: '8px',
                                        cursor: isRunning ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        transition: 'all 0.2s',
                                        color: contentTypes.video ? '#fff' : 'var(--text-muted)'
                                    }}
                                >
                                    {contentTypes.video ? <CheckCircle size={18} color="var(--accent-orange)" /> : <Video size={18} />}
                                    <span style={{ fontSize: '14px', fontWeight: 500 }}>Vídeos</span>
                                </div>
                            </div>
                        </div>

                        <div className="divider-line" style={{ height: '1px', background: 'var(--border-dark)', margin: '24px 0' }} />

                        {/* Substituições */}
                        <div className="config-section">
                            <div className="input-group-pro">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                        <input
                                            type="checkbox"
                                            checked={replacements.enableLinkReplacement}
                                            onChange={(e) => setReplacements({ ...replacements, enableLinkReplacement: e.target.checked })}
                                            disabled={isRunning}
                                            style={{ width: '16px', height: '16px', accentColor: 'var(--accent-orange)' }}
                                        />
                                        Substituição de Links
                                    </label>
                                </div>

                                {replacements.enableLinkReplacement && (
                                    <div className="input-wrapper fade-in" style={{ marginTop: '8px' }}>
                                        <Link size={16} color="#aaa" />
                                        <input
                                            placeholder="https://seu-link-novo.com"
                                            value={replacements.newLink}
                                            onChange={e => setReplacements({ ...replacements, newLink: e.target.value })}
                                            disabled={isRunning}
                                            style={{ fontSize: '14px' }}
                                        />
                                    </div>
                                )}
                                <p style={{ fontSize: '11px', color: '#666', marginTop: '6px', marginLeft: '24px' }}>
                                    Quando ativo, todos os links encontrados nas mensagens serão substituídos por este link.
                                </p>
                            </div>

                            <div className="input-group-pro" style={{ marginTop: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                        <input
                                            type="checkbox"
                                            checked={replacements.enableUserReplacement}
                                            onChange={(e) => setReplacements({ ...replacements, enableUserReplacement: e.target.checked })}
                                            disabled={isRunning}
                                            style={{ width: '16px', height: '16px', accentColor: 'var(--accent-orange)' }}
                                        />
                                        Substituição de @Usuario
                                    </label>
                                </div>

                                {replacements.enableUserReplacement && (
                                    <div className="input-wrapper fade-in" style={{ marginTop: '8px' }}>
                                        <AtSign size={16} color="#aaa" />
                                        <input
                                            placeholder="@SeuUsuarioNovo"
                                            value={replacements.newUser}
                                            onChange={e => setReplacements({ ...replacements, newUser: e.target.value })}
                                            disabled={isRunning}
                                            style={{ fontSize: '14px' }}
                                        />
                                    </div>
                                )}
                                <p style={{ fontSize: '11px', color: '#666', marginTop: '6px', marginLeft: '24px' }}>
                                    Substitui menções (@usuario) encontrado nas mensagens.
                                </p>
                            </div>

                            {/* Translation Settings */}
                            <div className="input-group-pro" style={{ marginTop: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                        <input
                                            type="checkbox"
                                            checked={replacements.enableTranslation}
                                            onChange={(e) => setReplacements({ ...replacements, enableTranslation: e.target.checked })}
                                            disabled={isRunning}
                                            style={{ width: '16px', height: '16px', accentColor: 'var(--accent-orange)' }}
                                        />
                                        Tradução Automática
                                    </label>
                                    <span style={{ fontSize: '10px', color: '#06b6d4', background: 'rgba(6, 182, 212, 0.1)', padding: '4px 8px', borderRadius: '4px', fontWeight: '600', letterSpacing: '0.05em' }}>IDIOMA</span>
                                </div>

                                {replacements.enableTranslation && (
                                    <div className="input-wrapper fade-in" style={{ marginTop: '8px' }}>
                                        <Languages size={16} color="#aaa" />
                                        <select
                                            value={replacements.targetLanguage}
                                            onChange={e => setReplacements({ ...replacements, targetLanguage: e.target.value })}
                                            disabled={isRunning}
                                        >
                                            <option value="">Selecione o idioma</option>
                                            <option value="en">🇬🇧 Inglês</option>
                                            <option value="es">🇪🇸 Espanhol</option>
                                        </select>
                                    </div>
                                )}
                                <p style={{ fontSize: '11px', color: '#666', marginTop: '6px', marginLeft: '24px' }}>
                                    Traduz automaticamente o texto das mensagens para o idioma selecionado.
                                </p>
                            </div>
                        </div>

                        {/* Botão de Ação */}
                        <div className="form-actions mt-6" style={{ marginTop: '32px' }}>
                            {!isRunning ? (
                                <GlassButton className="w-full" onClick={handleStart} loading={loading}>
                                    <Play size={18} fill="currentColor" /> Iniciar Clonagem Segura
                                </GlassButton>
                            ) : (
                                <GlassButton className="w-full stop-btn" onClick={handleStop} style={{ background: 'rgba(239, 68, 68, 0.2)', borderColor: '#ef4444' }}>
                                    <Pause size={18} fill="currentColor" /> Parar / Pausar
                                </GlassButton>
                            )}
                        </div>
                    </div>

                    {/* Painel de Status (Direita) */}
                    <div className="right-panel-stack" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Aviso de Segurança */}
                        <div className="info-card safe-mode" style={{
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 95, 70, 0.2) 100%)',
                            border: '1px solid #059669',
                            borderRadius: '12px',
                            padding: '24px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', color: '#34d399', fontWeight: 700 }}>
                                <ShieldCheck size={24} />
                                <span>Modo Seguro Ativado</span>
                            </div>
                            <p style={{ fontSize: '13px', color: 'rgba(209, 250, 229, 0.7)', lineHeight: 1.6, margin: 0 }}>
                                O sistema gerencia automaticamente a velocidade de clonagem para evitar bloqueios temporários (FloodWait) do Telegram.
                                Sente e relaxe, nós cuidamos da segurança da sua conta.
                            </p>
                        </div>

                        {/* Status da Clonagem */}
                        <div className="section-card status-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <div className="card-header">
                                <span className="card-title">Status da Operação</span>
                                {status?.status === 'running' && <Activity size={18} className="pulse text-emerald-400" color="#34d399" />}
                                {status?.status === 'stopped' && <XCircle size={18} color="#94a3b8" />}
                                {status?.status === 'completed' && <CheckCircle size={18} color="#34d399" />}
                                {status?.status === 'error' && <AlertTriangle size={18} color="#ef4444" />}
                            </div>

                            {status && status.status !== 'idle' ? (
                                <div className="status-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1 }}>
                                    <div className="status-metric-large" style={{ textAlign: 'center', padding: '20px', background: 'rgba(0,0,0,0.2)', borderRadius: '12px' }}>
                                        <span className="value" style={{ display: 'block', fontSize: '42px', fontWeight: 800, color: '#fff' }}>{status.processed_count}</span>
                                        <span className="label" style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mensagens Processadas</span>
                                    </div>

                                    <div className="progress-bar-container">
                                        <div className="progress-bar" style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
                                            <div
                                                className={`progress-fill ${status.status === 'running' ? 'animated' : ''}`}
                                                style={{ width: '100%', height: '100%', background: 'var(--accent-gradient)' }}
                                            ></div>
                                        </div>
                                        <span className="progress-text" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                            {statusLabel}
                                        </span>
                                    </div>

                                    <div className="log-window-mini" style={{ marginTop: 'auto', background: '#000', borderRadius: '8px', padding: '12px', height: '150px', overflowY: 'auto', border: '1px solid var(--border-light)' }}>
                                        <div className="log-lines">
                                            <div className="log-line" style={{ fontSize: '12px', fontFamily: 'monospace', color: '#aaa', marginBottom: '4px' }}>
                                                <span className="time" style={{ color: '#555', marginRight: '8px' }}>[{new Date().toLocaleTimeString()}]</span>
                                                <span className="msg">{status.last_message || "Aguardando atualizações..."}</span>
                                            </div>
                                            {status.errors_count ? (
                                                <div className="log-line error" style={{ color: '#ef4444', fontSize: '12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginTop: '8px' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <AlertTriangle size={12} />
                                                        {status.errors_count} erros detectados (auto-recuperação ativa)
                                                    </span>
                                                    {status.last_error_message ? (
                                                        <span className="error-detail" style={{ fontSize: '11px', opacity: 0.9 }} title={status.last_error_message}>
                                                            {status.last_error_message.length > 70 ? status.last_error_message.slice(0, 70) + '…' : status.last_error_message}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-state-mini" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, opacity: 0.3 }}>
                                    <Activity size={32} color="#fff" style={{ marginBottom: '12px' }} />
                                    <p style={{ color: '#fff' }}>Nenhuma operação ativa</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {localIDFinderOpen && (
                <IDFinderModal
                    onClose={() => setLocalIDFinderOpen(false)}
                    accountId={selectedAccountId}
                    onSelectId={(id, target, name) => {
                        // Se o usuário clicou em "Usar na Origem" (target='from') OU abriu via busca de origem
                        if (target === 'from' || finderTarget === 'source') {
                            setSourceChat(id);
                            if (name) setSourceName(name);
                        }
                        // Se o usuário clicou em "Usar no Destino" (target='to') OU abriu via busca de destino
                        else if (target === 'to' || finderTarget === 'dest') {
                            setDestChat(id);
                            if (name) setDestName(name);
                        }
                        setLocalIDFinderOpen(false);
                    }}
                />
            )}
        </div>
    );
};
